import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "h4e_cookie_consent_v1";
const FONT_LINK_ID = "h4e-google-fonts";

// Categories — match Borlabs structure (Essenziell + Externe Medien)
export const CATEGORY_ESSENTIAL = "essential";
export const CATEGORY_EXTERNAL = "external_media";

const DEFAULT_CONSENT = {
  [CATEGORY_ESSENTIAL]: true,
  [CATEGORY_EXTERNAL]: false,
  decided: false,
  ts: null,
};

function loadConsent() {
  if (typeof window === "undefined") return DEFAULT_CONSENT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONSENT;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_CONSENT, ...parsed, decided: true };
  } catch {
    return DEFAULT_CONSENT;
  }
}

function saveConsent(state) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...state, ts: new Date().toISOString() }),
    );
  } catch {
    /* ignore quota/private mode */
  }
}

const ConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const c = loadConsent();
    setConsent(c);
    setHydrated(true);
    if (!c.decided) setOpen(true);
  }, []);

  // Dynamically inject (or remove) Google Fonts based on external media consent.
  useEffect(() => {
    if (!hydrated) return;
    const head = document.head;
    const existing = document.getElementById(FONT_LINK_ID);
    if (consent[CATEGORY_EXTERNAL]) {
      if (!existing) {
        const link = document.createElement("link");
        link.id = FONT_LINK_ID;
        link.rel = "stylesheet";
        link.href =
          "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
        head.appendChild(link);
      }
    } else if (existing) {
      existing.remove();
    }
  }, [consent, hydrated]);

  const persist = useCallback((next) => {
    setConsent({ ...next, decided: true });
    saveConsent({ ...next, decided: true });
    setOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ [CATEGORY_ESSENTIAL]: true, [CATEGORY_EXTERNAL]: true });
  }, [persist]);

  const acceptEssentialOnly = useCallback(() => {
    persist({ [CATEGORY_ESSENTIAL]: true, [CATEGORY_EXTERNAL]: false });
  }, [persist]);

  const saveSelection = useCallback(
    (selection) => {
      persist({
        [CATEGORY_ESSENTIAL]: true,
        [CATEGORY_EXTERNAL]: !!selection[CATEGORY_EXTERNAL],
      });
    },
    [persist],
  );

  const reopen = useCallback(() => setOpen(true), []);

  const value = useMemo(
    () => ({ consent, open, acceptAll, acceptEssentialOnly, saveSelection, reopen, hydrated }),
    [consent, open, acceptAll, acceptEssentialOnly, saveSelection, reopen, hydrated],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}
